import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Divider, Link, Modal, Skeleton, Svg, Text } from '@hopskipdrive/react-components';

import NoDriverVehiclePhoto from '../../../../../assets/images/car-side.png';
import { Card } from '../../../../../shared/components/Card';
import { colorTokens } from '../../../../../shared/components/variables';
import { useToast } from '../../../../../shared/contexts/ToastContext';
import { AssignDriverModal } from '../../../../components/AssignDriverModal';
import { CarePartnerCard } from '../../../../components/CarePartnerCard';
import { DriverSearch } from '../../../../components/DriverSearch';
import { useAssignDriverSubmit } from '../../../../hooks/useAssignDriverSubmit';
import { useDriverDetailsQuery } from '../../../../hooks/useDriverDetailsQuery';
import { DriverSearchResult } from '../../../../types/driver';
import { Header, NoDriverVehicleContainer, StyledAlert } from '../styles';

interface NoDriverProps {
  adminRideUrl?: string;
  isCanceledOrInactive?: boolean;
  isCarePartnerRide?: boolean;
}

export const NoDriver: FC<NoDriverProps> = ({
  adminRideUrl,
  isCanceledOrInactive = false,
  isCarePartnerRide = false,
}) => {
  const { rideSlug } = useParams();
  const { data: driverDetailsResult, isFetching } = useDriverDetailsQuery({ rideSlug: rideSlug || '' });
  const [assignDriverModalOpen, setAssignDriverModalOpen] = useState<boolean>(false);
  const [closeCallModalOpen, setCloseCallModalOpen] = useState<boolean>(false);
  const [selectedDriver, setSelectedDriver] = useState<DriverSearchResult>();
  const [query, setQuery] = useState<string>('');
  const [driverQuery, setDriverQuery] = useState<string>('');
  const { addToast } = useToast();

  interface AssignDriverError extends Error {
    errors?: { key: string; message: string }[];
  }

  const { mutate, isPending } = useAssignDriverSubmit({
    rideSlug: rideSlug,
    successCallback: () => {
      setAssignDriverModalOpen(false);
    },
    errorCallback: (error: AssignDriverError) => {
      if (error.errors && error.errors[0]?.key === 'driver_schedule_close_call') {
        setCloseCallModalOpen(true);
      } else if (error.errors) {
        addToast({
          severity: 'error',
          message: error?.errors?.[0].message || '',
          alertProps: {
            icon: false,
            onClose: undefined,
            style: {
              border: `1px solid ${colorTokens.error.outlineBorder}`,
              background: colorTokens.primary.contrast,
              color: colorTokens.error.main,
            },
          },
        });
      }
      setAssignDriverModalOpen(false);
    },
  });

  const handleAssignDriverSubmit = (driverToSubmit, overrideConflict = false) => {
    let assignDriverPayload: { slug: string; override_schedule_conflict?: string } = {
      slug: driverToSubmit.slug,
    };

    if (overrideConflict) {
      assignDriverPayload = {
        ...assignDriverPayload,
        override_schedule_conflict: 'true',
      };
    }

    mutate(assignDriverPayload);
  };

  return isFetching ? (
    <Skeleton variant="text" width={690} height={50} data-testid="skeleton" />
  ) : (
    <Card>
      <Header>
        <Box style={{ display: 'flex', gap: '12px' }}>
          <Svg
            icon="directions-car-filled"
            style={{ display: 'block', height: '24px', width: '24px', alignSelf: 'center' }}
          />
          <Text variant="h6" style={{ color: colorTokens.primary.main }}>
            Driver
          </Text>
        </Box>
      </Header>
      {!isCanceledOrInactive && !isCarePartnerRide && (
        <Box display="flex" flexDirection="row" mb="20px" width="100%">
          <DriverSearch
            setSelectedDriver={setSelectedDriver}
            setAssignDriverModalOpen={setAssignDriverModalOpen}
            setQuery={setQuery}
            query={query}
            setDriverQuery={setDriverQuery}
            driverQuery={driverQuery}
          />
        </Box>
      )}
      {!isCarePartnerRide ? (
        <StyledAlert severity="warning" variant="filled" title="This ride doesn't have a driver yet" message="" />
      ) : (
        <StyledAlert
          severity="warning"
          variant="filled"
          title="Driver can be added in Admin"
          message=""
          action={
            <Link
              data-testid="admin-link"
              target="_blank"
              href={adminRideUrl}
              style={{
                textDecoration: 'none',
              }}
            >
              <Text variant="button">OPEN IN ADMIN</Text>
            </Link>
          }
        />
      )}
      {!isCarePartnerRide &&
        driverDetailsResult?.data?.tripDriverQualifications?.map((qualification, index) => (
          <StyledAlert
            key={`qualifications-${index}`}
            severity="warning"
            title={`This ride requires a driver with ${qualification} qualification`}
            message=""
            style={{ border: `1px solid ${colorTokens.warning.main}`, background: colorTokens.background.default }}
          />
        ))}
      {isCarePartnerRide && (
        <CarePartnerCard
          isFetching={isFetching}
          name={driverDetailsResult?.data?.carePartnerDetails?.name}
          certifiedDriversCount={driverDetailsResult?.data?.carePartnerDetails?.certifiedDriverCount}
          phoneNumber={driverDetailsResult?.data?.carePartnerDetails?.businessPhoneNumber}
          url={driverDetailsResult?.data?.carePartnerDetails?.adminCarePartnerDetailsLink}
        />
      )}
      <NoDriverVehicleContainer>
        <Box p={20}>
          <img src={NoDriverVehiclePhoto} alt="No Vehicle" />
        </Box>
      </NoDriverVehicleContainer>
      {selectedDriver && (
        <AssignDriverModal
          isOpen={assignDriverModalOpen}
          isPending={isPending}
          selectedDriver={selectedDriver}
          setQuery={setQuery}
          setDriverQuery={setDriverQuery}
          setSelectedDriver={setSelectedDriver}
          setAssignDriverModalOpen={setAssignDriverModalOpen}
          handleAssignDriverSubmit={handleAssignDriverSubmit}
        />
      )}
      <CloseCallModal
        isOpen={closeCallModalOpen}
        isPending={isPending}
        selectedDriver={selectedDriver}
        setCloseCallModalOpen={setCloseCallModalOpen}
        handleAssignDriverSubmit={handleAssignDriverSubmit}
      />
    </Card>
  );
};

const CloseCallModal = ({ isOpen, isPending, selectedDriver, setCloseCallModalOpen, handleAssignDriverSubmit }) => {
  return (
    <Modal open={isOpen}>
      <Modal.Title>
        This CareDriver cannot feasibly arrive to the pickup on time. Claim this ride on their behalf?
      </Modal.Title>
      <Divider />
      <Modal.Actions>
        <Button color="tonal" disabled={isPending} onClick={() => setCloseCallModalOpen(false)}>
          Never mind
        </Button>
        <Button color="primary" onClick={() => handleAssignDriverSubmit(selectedDriver, true)} loading={isPending}>
          OK
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
